import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  FileText,
  Clock,
  PoundSterling,
  Send,
  Image as ImageIcon,
  Wrench,
  Calendar,
  Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContractorRFQPage() {
  const { token } = useParams();
  
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    quote_total: '',
    lead_time: '',
    warranty_period: '',
    notes: ''
  });

  useEffect(() => {
    if (token) {
      loadRFQ();
    } else {
      setError('Invalid quote request link');
      setLoading(false);
    }
  }, [token]);

  const loadRFQ = async () => {
    try {
      const res = await axios.get(`${API}/api/phs/contractor/rfq/${token}`);
      setRfq(res.data);
      
      if (res.data.already_submitted && res.data.submitted_quote) {
        setFormData({
          quote_total: res.data.submitted_quote.quote_total || '',
          lead_time: res.data.submitted_quote.lead_time || '',
          warranty_period: '',
          notes: res.data.submitted_quote.notes || ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quote request');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.quote_total || parseFloat(formData.quote_total) <= 0) {
      setError('Please enter a valid quote amount');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/phs/contractor/rfq/${token}/submit-quote`, {
        quote_total: parseFloat(formData.quote_total),
        lead_time: formData.lead_time,
        warranty_period: formData.warranty_period,
        notes: formData.notes,
        line_items: []
      });
      
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDeadline = (dateStr) => {
    if (!dateStr) return 'As soon as possible';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#0F766E] mx-auto mb-4" />
          <p className="text-gray-600">Loading quote request...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error && !rfq) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Request Unavailable</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted State
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Submitted</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your quote submission. Park Home Support will review all submissions and contact you if your quote is selected.
            </p>
            <div className="p-4 bg-gray-50 rounded-lg text-left">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">Your Quote:</span>
                <span className="font-bold text-[#0F766E]">£{parseFloat(formData.quote_total).toFixed(2)}</span>
              </div>
              {formData.lead_time && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Lead Time:</span>
                  <span className="font-medium">{formData.lead_time}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="contractor-rfq-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-[#0F766E] to-[#115E59] rounded-xl flex items-center justify-center shadow-lg">
            <Wrench className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Park Home Support</h1>
            <p className="text-gray-500">Contractor Quote Request</p>
          </div>
        </div>
      </div>

      <Card className="max-w-2xl mx-auto shadow-xl border-0">
        <CardHeader className="bg-gradient-to-r from-[#0F766E] to-[#115E59] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Quote Request</CardTitle>
              <CardDescription className="text-white/80">
                {rfq?.rfq_id} • {rfq?.task_reference}
              </CardDescription>
            </div>
            <Badge className="bg-white/20 text-white border-0">
              {rfq?.already_submitted ? 'Update Quote' : 'New Request'}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Welcome */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Hello {rfq?.contractor_name},</span><br />
              You've been invited to submit a quote for the following work. Please review the details and provide your best price.
            </p>
          </div>

          {/* Job Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#0F766E]" />
              Work Details
            </h3>
            
            <div className="grid gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500 uppercase tracking-wide">Trade Required</Label>
                <p className="font-medium text-gray-900 mt-1">{rfq?.trade_required}</p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500 uppercase tracking-wide">Description</Label>
                <p className="text-gray-800 mt-1">{rfq?.description}</p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500 uppercase tracking-wide">Response Deadline</Label>
                <p className="font-medium text-gray-900 mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDeadline(rfq?.deadline)}
                </p>
              </div>
            </div>
          </div>

          {/* Photos */}
          {rfq?.photos && rfq.photos.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-[#0F766E]" />
                Site Photos
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {rfq.photos.map((photo, idx) => (
                  <img 
                    key={idx}
                    src={photo}
                    alt={`Site photo ${idx + 1}`}
                    className="w-full h-24 object-cover rounded-lg border"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quote Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 pt-4 border-t">
              <PoundSterling className="h-4 w-4 text-[#0F766E]" />
              Your Quote
            </h3>

            {error && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote_total">Quote Amount (£) *</Label>
                <div className="relative">
                  <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="quote_total"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="pl-10"
                    value={formData.quote_total}
                    onChange={(e) => setFormData({...formData, quote_total: e.target.value})}
                    required
                    data-testid="quote-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lead_time">Lead Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="lead_time"
                    placeholder="e.g., 3-5 working days"
                    className="pl-10"
                    value={formData.lead_time}
                    onChange={(e) => setFormData({...formData, lead_time: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="warranty_period">Warranty Period</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="warranty_period"
                  placeholder="e.g., 12 months parts and labour"
                  className="pl-10"
                  value={formData.warranty_period}
                  onChange={(e) => setFormData({...formData, warranty_period: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional information, assumptions, or conditions..."
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            <Button 
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#0F766E] hover:bg-[#115E59]"
              data-testid="submit-quote-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {rfq?.already_submitted ? 'Update Quote' : 'Submit Quote'}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500">
              By submitting, you confirm this quote is valid for 30 days and includes all costs.
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-center text-sm text-gray-500 mt-6">
        © {new Date().getFullYear()} Park Home Support. Contractor Portal.
      </p>
    </div>
  );
}
